import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CTA } from "@/components/CTA";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, PackageOpen } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { usePageSEO, buildHreflang } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";
import { resolveAssetUrl } from "@/lib/media";

interface Solution {
  id: string;
  title: string;
  title_ar: string | null;
  slug: string;
  description: string | null;
  description_ar: string | null;
  thumbnail_image: string | null;
  category_id: string | null;
}

interface Category {
  id: string;
  name: string;
  slug: string;
}

export default function SolutionsIndex() {
  const { lang, localePath } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);
  const isAr = lang === "ar";

  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const ar = <T,>(enVal: T, arVal: T | null | undefined): T => {
    if (isAr && arVal) return arVal;
    return enVal;
  };

  const { canonical, hreflang } = buildHreflang("/solutions");
  useCmsPageSEO({
    title: isAr ? "حلول رقمية للأعمال في الكويت | وافاي" : "Digital Solutions for Kuwait Businesses | Wavai",
    description: isAr
      ? "استكشف الحلول المختارة من وافاي — Klaviyo، Google Workspace، Zoho، Kinsta، Notion والمزيد — مهيأة للأعمال في الكويت."
      : "Explore Wavai's curated solutions — Klaviyo, Google Workspace, Zoho, Kinsta, Notion and more — tailored for businesses operating in Kuwait.",
    canonical,
    hreflang,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [solRes, catRes] = await Promise.all([
          supabase.from("solutions").select("id, title, title_ar, slug, description, description_ar, thumbnail_image, category_id").order("sort_order"),
          supabase.from("solution_categories").select("id, name, slug").order("name"),
        ]);
        if (solRes.error) throw solRes.error;
        setSolutions(solRes.data || []);
        setCategories(catRes.data || []);
      } catch (e: any) {
        setError(e.message || "Failed to load solutions");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filtered = activeCategory
    ? solutions.filter((s) => s.category_id === activeCategory)
    : solutions;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="pt-32 pb-20 lg:pt-40 lg:pb-24 relative">
          <div className="absolute inset-0 bg-gradient-hero opacity-40" />
          <div className="container px-6 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-sm uppercase tracking-wider text-primary font-medium mb-4">{t("Our Solutions")}</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6 text-balance">
                {t("Tools & Platforms We")}
                <span className="text-gradient"> {t("Excel In")}</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t("We partner with the best tools in the industry to deliver powerful, integrated solutions for your business.")}
              </p>
            </div>
          </div>
        </section>

        {categories.length > 0 && (
          <section className="py-8">
            <div className="container px-6">
              <div className="flex flex-wrap gap-2 justify-center">
                <button
                  onClick={() => setActiveCategory(null)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    !activeCategory ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t("All")}
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      activeCategory === cat.id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-12 lg:py-32">
          <div className="container px-6">
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="p-6 rounded-2xl bg-card border border-border flex flex-col items-center gap-3">
                    <Skeleton className="h-12 w-12 rounded" />
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4 text-muted-foreground">
                <AlertCircle className="h-10 w-10 text-destructive" />
                <p className="text-lg font-medium">{t("Something went wrong")}</p>
                <p className="text-sm">{error}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4 text-muted-foreground">
                <PackageOpen className="h-10 w-10" />
                <p className="text-lg font-medium">{t("No solutions found")}</p>
                <p className="text-sm">{t("Check back soon, we're always adding new tools and platforms.")}</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filtered.map((solution) => (
                  <Link
                    key={solution.id}
                    to={localePath(`/solutions/${solution.slug}`)}
                    className="group p-6 rounded-2xl bg-card border border-border hover:border-primary/30 transition-all duration-300 shadow-card hover:shadow-glow flex flex-col items-start text-left"
                  >
                    {solution.thumbnail_image && (
                      <img
                        src={resolveAssetUrl(solution.thumbnail_image)}
                        alt={ar(solution.title, solution.title_ar)}
                        className="h-12 w-auto object-contain group-hover:scale-105 transition-transform"
                        loading="lazy"
                      />
                    )}
                    <h3 className="font-sans font-medium mb-2 mt-20 group-hover:text-primary transition-colors line-clamp-2 text-balance">
                      {ar(solution.title, solution.title_ar)}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-3">{ar(solution.description, solution.description_ar)}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <CTA />
      </main>
      <Footer />
    </div>
  );
}
